// src/calls/service/peer.js
const logPC = (svc, tag) => console.log('[CALLDBG][PC]', tag, debugPC(svc));

export async function ensurePeer(svc) {
    if (svc.pc) return svc.pc;
    const pc = new svc.media.RTCPeerConnection({ iceServers: svc.iceServers || [] });
    svc.pc = pc;
    console.log('[CALLDBG][PC] RTCPeerConnection created', { iceServers: svc.iceServers });

    // add local tracks
    svc.localStream?.getTracks?.().forEach(tr => {
        pc.addTrack(tr, svc.localStream);
        console.log('[CALLDBG][PC] addTrack', tr.kind, { enabled: tr.enabled });
    });

    // Игнорим авто-renegotiate
    pc.onnegotiationneeded = async () => {
        console.log('[CALLDBG] onnegotiationneeded ignored');
    };

    pc.onicecandidate = ({ candidate }) => {
        if (candidate) {
            console.log('[CALLDBG][PC] onicecandidate -> emit');
            svc.socket.emit('call:candidate', { callId: svc.callId, to: svc.partner, candidate });
        }
    };

    pc.ontrack = (e) => {
        e.streams[0].getTracks().forEach(t => svc.remoteStream.addTrack(t));
        svc.emitter.emit('remote-stream-updated', svc.remoteStream);
        console.log('[CALLDBG][PC] ontrack add', e.track?.kind || 'track');
    };

    pc.onicegatheringstatechange = () => logPC(svc, 'iceGatheringState ' + pc.iceGatheringState);
    pc.oniceconnectionstatechange = () => logPC(svc, 'iceConnectionState ' + pc.iceConnectionState);

    pc.onconnectionstatechange = () => {
        logPC(svc, 'connectionState ' + pc.connectionState);
        const s = pc.connectionState;
        if (s === 'connected') {
            svc.state = 'in-call';
            svc._connectedAt = svc._connectedAt || Date.now();
            svc._reconnecting = false;
            clearTimeout(svc._reconnectTimer);
            svc._reconnectTimer = null;
            svc.emitter.emit('connected', { startedAt: svc._connectedAt });
            svc.emitter.emit('ring:stop');
            startStats(svc);
            setupKA(svc); // безопасно
            svc._acquireWakeLock().catch(() => { });
            return;
        }
        if (s === 'disconnected') { svc._beginReconnection(false); return; }
        if (s === 'failed') { svc._beginReconnection(true); return; }
        if (s === 'closed') { svc._cleanup('closed'); }
    };

    // Попробуем открыть KA-канал заранее (инициатор). Не критично, если не выйдет.
    try {
        if (svc.isLeader) {
            const ch = pc.createDataChannel?.('ka', { ordered: true });
            if (ch) setupKA(svc, ch);
        }
    } catch { }

    // Если канал создастся со стороны пира
    pc.ondatachannel = (e) => {
        if (!e?.channel) return;
        if (e.channel.label === 'ka') setupKA(svc, e.channel);
    };

    return pc;
}

export async function makeOffer(svc) {
    await ensurePeer(svc);
    try {
        console.log('[CALLDBG][PC] _makeOffer: start');
        svc.makingOffer = true;
        const offer = await svc.pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: true });
        await svc.pc.setLocalDescription(offer);
        console.log('[CALLDBG][PC] setLocal(offer) OK, sig=', svc.pc.signalingState);
        svc.socket.emit('call:offer', { callId: svc.callId, to: svc.partner, sdp: offer });
        if (svc.state !== 'in-call') { svc.state = 'connecting'; svc.emitter.emit('connecting'); }
        svc.emitter.emit('ring:stop');
        console.log('[CALLDBG][PC] _makeOffer: done');
    } catch (e) {
        console.warn('[CALLDBG] makeOffer failed:', e?.message || e);
    } finally {
        svc.makingOffer = false;
    }
}

/** Идемпотентно. Можно звать без ch — сам попробует создать. */
export function setupKA(svc, ch) {
    let channel = ch;
    if (!channel) {
        try { channel = svc.pc?.createDataChannel?.('ka', { ordered: true }); } catch { /* noop */ }
    }
    if (!channel) return;
    if (svc._kaChannel === channel && channel.readyState === 'open') return;

    try { clearInterval(svc._kaTimer); } catch { }
    svc._kaTimer = null;

    svc._kaChannel = channel;

    channel.onopen = () => {
        console.log('[CALLDBG][PC] KA channel open');
        try { clearInterval(svc._kaTimer); } catch { }
        svc._kaTimer = setInterval(() => {
            try { channel.readyState === 'open' && channel.send('ping:' + Date.now()); } catch { }
        }, 10_000);
    };

    channel.onclose = () => {
        console.log('[CALLDBG][PC] KA channel close');
        try { clearInterval(svc._kaTimer); } catch { }
        svc._kaTimer = null;
    };

    channel.onerror = (e) => console.log('[CALLDBG][PC] KA channel error', e);
    channel.onmessage = () => { /* noop */ };
}

export function startStats(svc) {
    clearInterval(svc._statsTimer);
    let prev = { bytesIn: 0, bytesOut: 0, ts: 0, lostIn: 0, recIn: 0 };
    svc._statsTimer = setInterval(async () => {
        if (!svc.pc) return;
        try {
            const stats = await svc.pc.getStats();
            let bytesIn = 0, bytesOut = 0, rtt = 0, jitter = 0, lostIn = 0, recIn = 0;

            stats.forEach(r => {
                if (r.type === 'inbound-rtp' && !r.isRemote && r.kind === 'audio') {
                    bytesIn += (r.bytesReceived || 0);
                    lostIn += (r.packetsLost || 0);
                    recIn += (r.packetsReceived || 0);
                    jitter = Math.max(jitter, (r.jitter || 0) * 1000);
                }
                if (r.type === 'outbound-rtp' && !r.isRemote && r.kind === 'audio') {
                    bytesOut += (r.bytesSent || 0);
                }
                if (r.type === 'candidate-pair' && r.state === 'succeeded') {
                    rtt = Math.max(rtt, (r.currentRoundTripTime || 0) * 1000);
                }
            });

            const now = Date.now();
            const dt = Math.max(1, now - (prev.ts || now));
            const bitrateIn = (bytesIn - prev.bytesIn) * 8 * 1000 / dt;
            const bitrateOut = (bytesOut - prev.bytesOut) * 8 * 1000 / dt;
            const dLost = Math.max(0, lostIn - prev.lostIn);
            const dRec = Math.max(0, recIn - prev.recIn);
            const lossPct = dRec > 0 ? (dLost / (dRec + dLost)) * 100 : 0;

            let level = 5;
            if (lossPct > 5 || rtt > 350 || bitrateIn < 8000) level = 2;
            if (lossPct > 10 || rtt > 600 || bitrateIn < 4000) level = 1;
            if (lossPct > 2 || rtt > 200 || bitrateIn < 16000) level = 3;
            if (lossPct > 1 || rtt > 120 || bitrateIn < 24000) level = 4;

            prev = { bytesIn, bytesOut, ts: now, lostIn, recIn };

            svc.emitter.emit('quality', {
                level,
                rtt: Math.round(rtt),
                jitter: Math.round(jitter),
                lossPct: Math.round(lossPct * 10) / 10,
                bitrateIn: Math.round(bitrateIn / 1000),
                bitrateOut: Math.round(bitrateOut / 1000),
            });

            if (bitrateIn === 0 && ((now - (svc._lastBitrateNudge || 0)) > 20_000)) {
                svc._lastBitrateNudge = now;
                if (svc.isLeader) svc._maybeIceRestart('zero-bitrate');
            }

            if ((now / 10000 | 0) !== ((prev.ts || 0) / 10000 | 0)) {
                try { svc.reportQuality(level); } catch { }
            }
        } catch { }
    }, 2000);
}

export function cleanupPeer(svc, reason) {
    clearTimeout(svc._ringTimer);
    clearInterval(svc._statsTimer);
    clearTimeout(svc._reconnectTimer);
    clearInterval(svc._kaTimer);
    svc._statsTimer = null;
    svc._reconnectTimer = null;
    svc._kaTimer = null;

    svc._reconnecting = false;
    svc._reconnectStartedAt = 0;

    svc.emitter.emit('ring:stop');
    try { svc.localStream?.getTracks?.().forEach(t => t.stop()); } catch { }
    try { svc.pc?.getSenders?.().forEach(s => s.track && s.track.stop()); } catch { }
    try { svc._kaChannel?.close?.(); } catch { }
    try { svc.pc?.close?.(); } catch { }

    svc._kaChannel = null;
    svc.pc = null;
    svc.localStream = null;
    svc.remoteStream = new svc.media.MediaStream();
    svc.state = 'idle';
    svc.partner = null;
    svc.callId = null;

    svc.makingOffer = false;
    svc.ignoreOffer = false;

    svc._releaseWakeLock();
    console.log('[CALLDBG] _cleanup', reason);
    svc.emitter.emit('ended', { reason });
}

export function debugPC(svc) {
    const pc = svc.pc;
    return pc ? {
        iceGatheringState: pc.iceGatheringState,
        iceConnectionState: pc.iceConnectionState,
        connectionState: pc.connectionState,
        signalingState: pc.signalingState
    } : {};
}
